import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CircleCheck,
  CircleOff,
  Edit2,
  Eye,
  EyeOff,
  Layers,
  TriangleAlert,
  Plus,
  Trash2,
  Users,
  X,
  Save,
  Loader,
  ToggleLeft,
  ToggleRight,
  User as UserIcon,
  UserCog,
  UserX,
} from 'lucide-react';
import {
  useSection,
  useServices,
  useDeleteConfirmation,
  useConfirmation,
  useToast,
  getErrorMessage,
} from '@/app/providers';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import { usePaginatedList, useCrudNotifications } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Input } from '../atoms/Input';
import { Button } from '../atoms/Button';
import { Checkbox } from '../atoms/Checkbox';
import { DataTable, RowActionsMenu, type ColumnDef } from '@/core/ui/components';
import { SearchableSelect, type SelectOption } from '@/core/ui/components/SearchableSelect';
import { Section, SectionBankInfo, SectionStatus, User, UserRole, SectionAdmin } from '@/shared/types';

/**
 * `GET /sections` ya trae unitsCount/residentsCount/assignedMembersCount, así que la
 * fila es la propia sección: no hay un `stats` que pedir aparte.
 */
type SectionRow = Section;

interface SectionFormState {
  id?: string;
  name: string;
  code: string;
  description?: string;
  status: SectionStatus;
  maintenanceFee?: number | null;
  /** `null` = usa la cuenta bancaria del residencial. */
  bankInfo?: SectionBankInfo | null;
}

const EMPTY_BANK_INFO: SectionBankInfo = { bankName: '', accountHolder: '', clabe: '' };

interface AdminFormState {
  mode: 'existing' | 'new';
  memberId: string;
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const EMPTY_FORM: SectionFormState = {
  name: '',
  code: '',
  description: '',
  status: SectionStatus.ACTIVE,
};

const EMPTY_ADMIN_FORM: AdminFormState = {
  mode: 'existing',
  memberId: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
};

const SCRUB_PHONE_TO_DIGITS = (val: string) => val.replace(/\D/g, '').slice(0, 10);

export const AdminSections = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const ability = useAbility();
  const { confirmDelete } = useDeleteConfirmation();
  const { confirm } = useConfirmation();
  const { sectionRepo, userRepo, currentUser } = useServices();
  // Admin de sección (§25.3): solo ve su sección; no la crea, edita, cambia de
  // estado ni elimina, ni gestiona sus admins.
  const actsAsSectionAdmin = isSectionAdmin(currentUser);
  const toast = useToast();
  const { setSelectedSectionId, refreshSections } = useSection();
  const {
    withCreateNotification,
    withUpdateNotification,
    withDeleteNotification,
    withNotification,
  } = useCrudNotifications({
    entityName: 'Sección',
  });

  const canUseSectionAction = (action: 'create' | 'update' | 'delete') =>
    ability.can('manage', 'all') || ability.can(action, 'Section');
  const canCreateSection = canUseSectionAction('create') && !actsAsSectionAdmin;
  const canUpdateSection = canUseSectionAction('update') && !actsAsSectionAdmin;
  const canDeleteSection = canUseSectionAction('delete') && !actsAsSectionAdmin;

  const [adminsMap, setAdminsMap] = useState<Record<string, SectionAdmin[]>>({});
  // Unidades sin sección: el aviso de que una migración quedó a medias. Sus
  // residentes dejan de ver el contenido de sección sin que nada lo indique.
  const [unassignedUnits, setUnassignedUnits] = useState(0);
  const [detailSection, setDetailSection] = useState<SectionRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm] = useState<SectionFormState>(EMPTY_FORM);
  const [originalStatus, setOriginalStatus] = useState<SectionStatus>(SectionStatus.ACTIVE);
  const [adminForm, setAdminForm] = useState<AdminFormState>(EMPTY_ADMIN_FORM);
  // Errores por campo. Antes toda la validación salía por toast, lejos del
  // campo y sin decir cuál era: el usuario tenía que adivinar.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState(false);
  /** El admin solo se edita bajo demanda cuando la sección ya tiene uno. */
  const [isAdminBlockOpen, setIsAdminBlockOpen] = useState(false);

  /** Al corregir un campo su error desaparece, sin esperar a reenviar. */
  const updateAdminForm = (patch: Partial<AdminFormState>) => {
    setAdminForm((prev) => ({ ...prev, ...patch }));
    const touched = Object.keys(patch);
    setErrors((prev) => {
      if (!touched.some((k) => prev[k])) return prev;
      const next = { ...prev };
      touched.forEach((k) => delete next[k]);
      return next;
    });
  };
  const [staffOptions, setStaffOptions] = useState<SelectOption[]>([]);
  /** Distingue "falló la carga" de "no hay nadie asignable" bajo el select. */
  const [staffLoadFailed, setStaffLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: sections,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh: refreshList,
  } = usePaginatedList<Section>({
    fetchFn: (params) => sectionRepo.getSections(params),
    errorTitle: 'Error al cargar secciones',
  });
  // El catálogo global (SectionContext) se carga una vez por sesión: sin
  // refrescarlo, Unidades y los demás selectores no veían la sección recién
  // creada hasta recargar la página.
  const refresh = () => {
    refreshList();
    refreshSections();
  };

  const sectionsWithStats: SectionRow[] = sections;

  const currentAdmins = useMemo(
    () => (isEditMode && form.id ? adminsMap[form.id] ?? [] : []),
    [isEditMode, form.id, adminsMap],
  );
  // Los que ya administran la sección no se ofrecen otra vez.
  const adminOptions = useMemo<SelectOption[]>(
    () => staffOptions.filter((o) => !currentAdmins.some((a) => a.memberId === o.value)),
    [staffOptions, currentAdmins],
  );

  useEffect(() => {
    let cancelled = false;
    void sectionRepo
      .getTenantStats()
      .then(({ unassignedUnitsCount }) => {
        if (!cancelled) setUnassignedUnits(unassignedUnitsCount);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sectionRepo, sections]);

  // El nombre y email del admin no viajan en el catálogo, solo `assignedMembersCount`,
  // así que se pide el detalle únicamente de las secciones que tienen admin en
  // vez de una llamada por fila.
  //
  // Si el conteo NO viene (backend anterior a que se añadiera), se pide igual:
  // tratar el campo ausente como 0 dejaba la columna de administradores
  // permanentemente en "Sin admin asignado" aunque la asignación funcionara.
  useEffect(() => {
    let cancelled = false;
    const withAdmin = sections.filter(
      (section) => section.assignedMembersCount === undefined || section.assignedMembersCount > 0,
    );
    const loadAdmins = async () => {
      const next: Record<string, SectionAdmin[]> = {};
      await Promise.all(
        withAdmin.map(async (section) => {
          try {
            next[section.id] = await sectionRepo.getAdmins(section.id);
          } catch {
            next[section.id] = [];
          }
        }),
      );
      if (!cancelled) setAdminsMap(next);
    };
    void loadAdmins();
    return () => {
      cancelled = true;
    };
  }, [sections, sectionRepo]);

  const loadStaffOptions = useCallback(async () => {
    setStaffLoadFailed(false);
    try {
      // El backend rechaza limit > MAX_LIMIT (100); pedir 200 devolvía 400 y el
      // catch dejaba el selector de admins vacío.
      //
      // Solo admins de sección: su `master_users.tenant_role` es SECTION_ADMIN,
      // así que el filtro por rol sí los encuentra (los ADMIN tienen NULL ahí).
      // Puede administrar varias secciones, así que se ofrece aunque ya tenga otra.
      const res = await userRepo.getUsers({ limit: 100, role: 'SECTION_ADMIN' });
      // Solo miembros reales del tenant (tenant_members.id) pueden ser admin de
      // sección: el backend resuelve el master user vía esa fila (§25.6).
      setStaffOptions(
        res.data
          .filter((u) => u.memberId)
          .map((u) => ({
            value: u.memberId!,
            label: `${u.name}${u.email ? ` (${u.email})` : ''}`,
          })),
      );
    } catch (error) {
      // Vaciar en silencio deja un "Sin opciones" indistinguible de "no hay
      // nadie asignable", que es lo que hizo que este fallo tardara dos rondas
      // en diagnosticarse.
      setStaffLoadFailed(true);
      setStaffOptions([]);
      toast.error(getErrorMessage(error));
    }
  }, [userRepo, toast]);

  const handleOpenCreate = () => {
    if (!canCreateSection) return;
    setForm(EMPTY_FORM);
    setOriginalStatus(SectionStatus.ACTIVE);
    setAdminForm(EMPTY_ADMIN_FORM);
    setErrors({});
    setShowPasswords(false);
    // Al crear el bloque va abierto, pero asignar admin es opcional.
    setIsAdminBlockOpen(true);
    setIsEditMode(false);
    setIsModalOpen(true);
    void loadStaffOptions();
  };

  const handleOpenEdit = (section: Section) => {
    if (!canUpdateSection) return;
    setForm({
      id: section.id,
      name: section.name,
      code: section.code,
      description: section.description ?? undefined,
      status: section.status,
      maintenanceFee: section.maintenanceFee ?? null,
      bankInfo: section.bankInfo ?? null,
    });
    setOriginalStatus(section.status);
    setErrors({});
    setShowPasswords(false);
    setIsAdminBlockOpen(false);
    // Una sección admite varios admins (§25.2): el select agrega uno nuevo.
    setAdminForm(EMPTY_ADMIN_FORM);
    setIsEditMode(true);
    setIsModalOpen(true);
    void loadStaffOptions();
  };

  /**
   * Devuelve un error por campo en vez de un único mensaje: así el error se
   * pinta debajo del input que lo causó (`Input` ya soporta `error`) y se
   * puede mover el foco al primer campo inválido.
   */
  const validateAdminForm = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (adminForm.mode === 'existing') {
      if (!adminForm.memberId) next.memberId = 'Selecciona el administrador de la sección';
      return next;
    }
    if (!adminForm.name.trim()) next.name = 'El nombre del administrador es obligatorio';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminForm.email)) next.email = 'Ingresa un email válido';
    if (adminForm.password.length < 8) next.password = 'Mínimo 8 caracteres';
    if (adminForm.password !== adminForm.confirmPassword) {
      next.confirmPassword = 'Las contraseñas no coinciden';
    }
    return next;
  };

  const assignOrCreateAdmin = async (sectionId: string) => {
    if (adminForm.mode === 'new') {
      // Nace como admin de sección ya asignado a esta sección: `POST /users`
      // acepta `sectionIds` para ese rol, sin promoción posterior.
      await userRepo.createUser({
        name: adminForm.name.trim(),
        email: adminForm.email.trim(),
        phone: adminForm.phone || undefined,
        role: UserRole.SECTION_ADMIN,
        tenantRole: 'SECTION_ADMIN',
        status: 'ACTIVE',
        password: adminForm.password,
        sectionIds: [sectionId],
      } as unknown as Omit<User, 'id'>);
      return;
    }
    const memberId = adminForm.memberId;
    if (!memberId) {
      throw new Error('Falta el miembro al que asignar la sección');
    }
    await sectionRepo.assignSectionAdmin(sectionId, memberId);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode ? !canUpdateSection : !canCreateSection) return;

    // Al crear, el admin es opcional: solo se valida si se empezó a llenar.
    // Al editar solo se valida si se abrió "Agregar" administrador.
    const adminFormEmpty =
      adminForm.mode === 'existing'
        ? !adminForm.memberId
        : ![adminForm.name, adminForm.email, adminForm.phone, adminForm.password, adminForm.confirmPassword]
            .some((value) => value.trim());
    const skipAdminValidation = isEditMode ? !isAdminBlockOpen : adminFormEmpty;
    const fieldErrors = skipAdminValidation ? {} : validateAdminForm();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      // Foco al primer campo inválido: el error inline no sirve si el campo
      // quedó fuera de la zona visible del modal.
      const firstInvalid = Object.keys(fieldErrors)[0];
      document.querySelector<HTMLElement>(`[data-field="${firstInvalid}"]`)?.focus();
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode && form.id) {
        const { id, status: _status, ...data } = form;
        await withUpdateNotification(async () => {
          await sectionRepo.updateSection(id, data);
          // El status no viaja por UpdateSectionDto: se persiste por su endpoint
          // dedicado si cambió en el formulario (igual que en el create).
          if (form.status && form.status !== originalStatus) {
            await sectionRepo.updateSectionStatus(id, form.status);
          }
          // Agregar un administrador a la sección (§25.6); los actuales se quedan.
          const isAlreadyAdmin = adminsMap[id]?.some((a) => a.memberId === adminForm.memberId);
          if (adminForm.mode === 'new' && isAdminBlockOpen) {
            await assignOrCreateAdmin(id);
          } else if (adminForm.memberId && !isAlreadyAdmin) {
            await sectionRepo.assignSectionAdmin(id, adminForm.memberId);
          }
        });
      } else {
        await withCreateNotification(async () => {
          const section = await sectionRepo.createSection(form);
          if (!adminFormEmpty) await assignOrCreateAdmin(section.id);
        });
      }
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (section: Section) => {
    if (!canUpdateSection) return;
    const nextStatus = section.status === SectionStatus.ACTIVE ? SectionStatus.INACTIVE : SectionStatus.ACTIVE;
    try {
      await withUpdateNotification(() => sectionRepo.updateSectionStatus(section.id, nextStatus));
      refresh();
    } catch {
      // El hook ya muestra el error
    }
  };

  const handleDelete = async (section: Section) => {
    if (!canDeleteSection) return;

    const confirmed = await confirmDelete({
      itemName: section.name,
      itemType: 'Sección',
      // `confirmDelete` se come el error y cierra el modal sin avisar (ver
      // DeleteConfirmationContext): sin envolverlo, el 409 de "la sección tiene
      // información relacionada" solo se veía en la pestaña de red.
      onConfirm: () => withDeleteNotification(() => sectionRepo.deleteSection(section.id)),
    });
    if (confirmed) {
      refresh();
    }
  };

  const handleOpenDetail = (section: SectionRow) => {
    setDetailSection(section);
  };

  const handleRemoveAdmin = async (section: SectionRow, admin: SectionAdmin) => {
    if (!canUpdateSection) return;
    // Retirar no borra al usuario: solo le quita la administración de esta
    // sección (sus otras secciones y su cuenta siguen intactas).
    const name = admin.name ?? admin.email ?? admin.memberId;
    const confirmed = await confirm({
      title: 'Retirar administrador',
      message: `${name} dejará de administrar la sección "${section.name}". Su cuenta no se elimina.`,
      confirmText: 'Retirar',
    });
    if (!confirmed) return;
    try {
      await withNotification(
        () => sectionRepo.removeSectionAdmin(section.id, admin.memberId),
        'Administrador retirado de la sección',
      );
      refresh();
    } catch {
      // El toast de error ya lo mostró withNotification.
    }
  };

  const handleViewUnits = (section: SectionRow) => {
    setSelectedSectionId(section.id);
    navigate('/admin/unidades');
  };

  const handleViewResidents = (section: SectionRow) => {
    setSelectedSectionId(section.id);
    navigate('/admin/residentes');
  };

  // Escape cierra el modal abierto. Sin esto la única salida era la X, que
  // además no era alcanzable por teclado (no tenía foco visible).
  useEffect(() => {
    if (!isModalOpen && !detailSection) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isModalOpen) setIsModalOpen(false);
      else setDetailSection(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, detailSection]);

  const columns: ColumnDef<SectionRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Sección',
        cell: (section) => (
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-600/20 text-primary-800">
              <Layers size={16} />
            </div>
            <div>
              <div className="font-bold text-slate-800">{section.name}</div>
              <div className="text-xs text-slate-500 font-mono">{section.code}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'stats',
        header: 'Métricas',
        cell: (section) => (
          <div className="flex gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-600">
              <Building2 size={14} className="text-indigo-500 shrink-0" />
              <span className="tabular-nums font-medium text-slate-700">{section.unitsCount}</span>
              <span className="text-slate-500">unidades</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-600">
              <Users size={14} className="text-blue-500 shrink-0" />
              <span className="tabular-nums font-medium text-slate-700">
                {section.residentsCount}
              </span>
              <span className="text-slate-500">residentes</span>
            </span>
          </div>
        ),
      },
      {
        id: 'admins',
        header: 'Administradores',
        cell: (section) => {
          // `adminsMap` es la fuente de verdad; `assignedMembersCount` solo sirve para
          // decidir si hace falta pedir el detalle y para resolver el caso
          // "no hay admin" sin esperar. Cortar aquí por `assignedMembersCount ?? 0`
          // dejaba la columna en "Sin admin asignado" de forma permanente
          // contra un backend que todavía no devuelve ese campo.
          const admins = adminsMap[section.id];

          if (admins === undefined) {
            if (section.assignedMembersCount === 0) {
              return <span className="text-xs text-slate-500">Sin admin asignado</span>;
            }
            return (
              <span className="inline-block h-4 w-28 rounded bg-slate-100 animate-pulse" aria-hidden />
            );
          }

          if (admins.length === 0) {
            return <span className="text-xs text-slate-500">Sin admin asignado</span>;
          }
          return (
            <div className="flex flex-col gap-1">
              {admins.map((admin) => (
                <span
                  key={admin.memberId}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-700"
                >
                  <UserCog size={14} className="text-primary-500 shrink-0" />
                  <span className="truncate">{admin.name ?? admin.email ?? admin.memberId}</span>
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Estado',
        cell: (section) => (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              section.status === SectionStatus.ACTIVE
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}
          >
            {section.status === SectionStatus.ACTIVE ? (
              <CircleCheck size={12} className="text-emerald-500" />
            ) : (
              <CircleOff size={12} className="text-slate-400" />
            )}
            {section.status === SectionStatus.ACTIVE ? 'Activa' : 'Inactiva'}
          </span>
        ),
      },
    ],
    [adminsMap],
  );

  return (
    <>
      {unassignedUnits > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <TriangleAlert size={18} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              {unassignedUnits} unidad(es) sin sección
            </p>
            <p className="text-xs text-amber-700">
              Sus residentes no ven el contenido de ninguna sección hasta que se asignen.
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/admin/unidades')}>
            Asignarlas
          </Button>
        </div>
      )}

      <DataTable
        data={sectionsWithStats}
        columns={columns}
        keyExtractor={(section) => section.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Secciones"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar sección..."
        loading={loading}
        emptyMessage="No hay secciones registradas"
        emptyIcon={
          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
            <Layers className="w-8 h-8 text-primary-400" />
          </div>
        }
        actions={canCreateSection ? (
          <Button onClick={handleOpenCreate} icon={<Plus size={16} />}>
            Nueva sección
          </Button>
        ) : undefined}
        rowActions={(section) => (
          <RowActionsMenu>
            {(close) => (
              <>
                <button
                  onClick={() => { handleOpenDetail(section); close(); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Eye size={16} className="text-blue-500" /> Ver detalle
                </button>
                {canUpdateSection && (
                  <>
                    <button
                      onClick={() => { handleOpenEdit(section); close(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Edit2 size={16} className="text-amber-500" /> Editar
                    </button>
                    <button
                      onClick={() => { handleToggleStatus(section); close(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      {section.status === SectionStatus.ACTIVE ? (
                        <>
                          <ToggleLeft size={16} className="text-slate-500" /> Desactivar
                        </>
                      ) : (
                        <>
                          <ToggleRight size={16} className="text-emerald-500" /> Activar
                        </>
                      )}
                    </button>
                    {/* Uno por admin; un admin de sección no puede retirarse a sí mismo. */}
                    {(adminsMap[section.id] ?? [])
                      .filter((admin) => admin.masterUserId !== currentUser?.id)
                      .map((admin) => (
                        <button
                          key={admin.memberId}
                          onClick={() => { handleRemoveAdmin(section, admin); close(); }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <UserX size={16} className="text-slate-500 shrink-0" />
                          <span className="truncate">
                            Retirar a {admin.name ?? admin.email ?? admin.memberId}
                          </span>
                        </button>
                      ))}
                  </>
                )}
                {canDeleteSection && (
                  <>
                    <hr className="my-1 border-slate-100" />
                    <button
                      onClick={() => { handleDelete(section); close(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} /> Eliminar
                    </button>
                  </>
                )}
              </>
            )}
          </RowActionsMenu>
        )}
      />

      {detailSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="section-detail-title"
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
          >
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 id="section-detail-title" className="font-bold text-slate-800 text-lg">
                {detailSection.name}
              </h3>
              <button
                type="button"
                onClick={() => setDetailSection(null)}
                aria-label="Cerrar"
                className="p-2 -m-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide mb-2">
                    <Building2 size={14} /> Unidades
                  </div>
                  <div className="text-3xl font-extrabold text-slate-800 tabular-nums">
                    {detailSection.unitsCount}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide mb-2">
                    <Users size={14} /> Residentes
                  </div>
                  <div className="text-3xl font-extrabold text-slate-800 tabular-nums">
                    {detailSection.residentsCount}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-1">Cuenta bancaria</p>
                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  {detailSection.bankInfo
                    ? `Propia: ${detailSection.bankInfo.bankName} · CLABE ${detailSection.bankInfo.clabe}`
                    : 'Usa la cuenta del residencial'}
                </p>
              </div>

              {detailSection.description && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Descripción</p>
                  <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    {detailSection.description}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  icon={<Building2 size={16} />}
                  onClick={() => handleViewUnits(detailSection)}
                >
                  Consultar unidades
                </Button>
                <Button
                  variant="secondary"
                  icon={<Users size={16} />}
                  onClick={() => handleViewResidents(detailSection)}
                >
                  Consultar residentes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="section-form-title"
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
          >
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 id="section-form-title" className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar sección' : 'Nueva sección'}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Cerrar"
                className="p-2 -m-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex flex-col overflow-hidden">
              <div className="p-6 space-y-5 overflow-y-auto">
              <div>
                <span id="section-status-label" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Estado
                </span>
                <div
                  role="radiogroup"
                  aria-labelledby="section-status-label"
                  className="grid grid-cols-2 gap-3"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.status === SectionStatus.ACTIVE}
                    onClick={() => setForm({ ...form, status: SectionStatus.ACTIVE })}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                      form.status === SectionStatus.ACTIVE
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <ToggleRight size={20} /> Activa
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.status === SectionStatus.INACTIVE}
                    onClick={() => setForm({ ...form, status: SectionStatus.INACTIVE })}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                      form.status === SectionStatus.INACTIVE
                        ? 'border-slate-400 bg-slate-100 text-slate-700 font-bold'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <ToggleLeft size={20} /> Inactiva
                  </button>
                </div>
                {form.status === SectionStatus.INACTIVE && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    Una sección inactiva conserva su histórico pero deja de aceptar
                    unidades y residentes nuevos.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Nombre"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Torre A"
                  autoComplete="off"
                  required
                />
                <div>
                  <Input
                    label="Código"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="TORRE-A"
                    autoComplete="off"
                    className="font-mono"
                    required
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Letras, números y guiones. Se guarda en mayúsculas.
                  </p>
                </div>
              </div>
              <Input
                label="Descripción"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Torre principal del residencial"
                autoComplete="off"
              />
              <div>
                <Input
                  label="Cuota de mantenimiento"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.maintenanceFee ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, maintenanceFee: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })
                  }
                  placeholder="Cuota general"
                />
                <p className="mt-1 text-xs text-slate-500">Vacío: usa la cuota general de Ajustes.</p>
              </div>

              <div className="space-y-3">
                <Checkbox
                  label="Cuenta bancaria propia"
                  checked={!!form.bankInfo}
                  onChange={(checked) =>
                    setForm({ ...form, bankInfo: checked ? form.bankInfo ?? EMPTY_BANK_INFO : null })
                  }
                />
                {form.bankInfo ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      label="Banco"
                      value={form.bankInfo.bankName}
                      onChange={(e) => setForm({ ...form, bankInfo: { ...form.bankInfo!, bankName: e.target.value } })}
                      autoComplete="off"
                      required
                    />
                    <Input
                      label="Titular"
                      value={form.bankInfo.accountHolder}
                      onChange={(e) => setForm({ ...form, bankInfo: { ...form.bankInfo!, accountHolder: e.target.value } })}
                      autoComplete="off"
                      required
                    />
                    <Input
                      label="CLABE"
                      value={form.bankInfo.clabe}
                      onChange={(e) => setForm({ ...form, bankInfo: { ...form.bankInfo!, clabe: e.target.value.replace(/\D/g, '') } })}
                      inputMode="numeric"
                      pattern="\d{18}"
                      maxLength={18}
                      title="18 dígitos"
                      autoComplete="off"
                      className="font-mono"
                      required
                    />
                    <Input
                      label="Número de cuenta"
                      value={form.bankInfo.accountNumber ?? ''}
                      onChange={(e) => setForm({ ...form, bankInfo: { ...form.bankInfo!, accountNumber: e.target.value } })}
                      autoComplete="off"
                    />
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Usa la cuenta bancaria del residencial (Ajustes).</p>
                )}
              </div>

              <div className="border-t border-slate-100 pt-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <UserCog size={16} className="text-primary-500" />
                    <span className="text-sm font-semibold text-slate-700">
                      Administradores de la sección
                    </span>
                    {!isEditMode && <span className="text-xs text-slate-400">(opcional)</span>}
                  </div>
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={() => setIsAdminBlockOpen((v) => !v)}
                      aria-expanded={isAdminBlockOpen}
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700 rounded px-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      {isAdminBlockOpen ? 'Cancelar' : 'Agregar'}
                    </button>
                  )}
                </div>

                {/*
                  Divulgación progresiva: al editar, el formulario de sección son
                  3 campos y este bloque añade 5 más. Si la sección ya tiene
                  admin se muestra quién es y el formulario solo aparece al
                  pedirlo. Al crear sí va abierto, aunque asignar admin es
                  opcional: la sección puede nacer sin administrador.
                */}
                {isEditMode && !isAdminBlockOpen ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                    {currentAdmins.length > 0 ? (
                      <>
                        <UserIcon size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate">
                          {currentAdmins.map((a) => a.name ?? a.email ?? a.memberId).join(', ')}
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-500">Sin administrador asignado</span>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      role="radiogroup"
                      aria-label="Origen del administrador"
                      className="grid grid-cols-2 gap-3 mb-4"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={adminForm.mode === 'existing'}
                        onClick={() => updateAdminForm({ mode: 'existing' })}
                        className={`p-3 rounded-lg border flex flex-col items-center gap-2 text-center text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                          adminForm.mode === 'existing'
                            ? 'border-primary-500 bg-primary-50 text-primary-700 font-bold'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <UserIcon size={20} /> Seleccionar existente
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={adminForm.mode === 'new'}
                        onClick={() => updateAdminForm({ mode: 'new' })}
                        className={`p-3 rounded-lg border flex flex-col items-center gap-2 text-center text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                          adminForm.mode === 'new'
                            ? 'border-primary-500 bg-primary-50 text-primary-700 font-bold'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Plus size={20} /> Crear nuevo
                      </button>
                    </div>

                    {adminForm.mode === 'existing' ? (
                      <div data-field="memberId">
                        <SearchableSelect
                          label="Miembro del equipo"
                          options={adminOptions}
                          value={adminForm.memberId || null}
                          onChange={(v) => updateAdminForm({ memberId: v ?? '' })}
                          placeholder="Buscar por nombre o email..."
                          isClearable
                        />
                        {errors.memberId && (
                          <p className="mt-1 text-xs text-red-500" role="alert">
                            {errors.memberId}
                          </p>
                        )}
                        {adminOptions.length === 0 && (
                          <p className="mt-1 text-xs text-slate-500">
                            {staffLoadFailed
                              ? 'No se pudo cargar el personal. Cierra y vuelve a abrir.'
                              : 'No hay admins de sección todavía: crea uno con "Crear nuevo".'}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Input
                          label="Nombre del administrador"
                          data-field="name"
                          value={adminForm.name}
                          onChange={(e) => updateAdminForm({ name: e.target.value })}
                          placeholder="Carlos Ruiz"
                          error={errors.name}
                          autoComplete="off"
                          required
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input
                            label="Email"
                            type="email"
                            data-field="email"
                            inputMode="email"
                            value={adminForm.email}
                            onChange={(e) => updateAdminForm({ email: e.target.value })}
                            placeholder="admin@yawi.com"
                            error={errors.email}
                            autoComplete="off"
                            required
                          />
                          <Input
                            label="Teléfono"
                            type="tel"
                            inputMode="numeric"
                            value={adminForm.phone}
                            onChange={(e) =>
                              updateAdminForm({ phone: SCRUB_PHONE_TO_DIGITS(e.target.value) })
                            }
                            placeholder="5512345678"
                            autoComplete="off"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="relative flex items-center">
                            <Input
                              label="Contraseña"
                              type={showPasswords ? 'text' : 'password'}
                              data-field="password"
                              value={adminForm.password}
                              onChange={(e) => updateAdminForm({ password: e.target.value })}
                              placeholder="Mínimo 8 caracteres"
                              error={errors.password}
                              autoComplete="new-password"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPasswords((v) => !v)}
                              aria-label={showPasswords ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                              className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors mt-6"
                            >
                              {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          <Input
                            label="Confirmar contraseña"
                            type={showPasswords ? 'text' : 'password'}
                            data-field="confirmPassword"
                            value={adminForm.confirmPassword}
                            onChange={(e) => updateAdminForm({ confirmPassword: e.target.value })}
                            placeholder="Repite la contraseña"
                            error={errors.confirmPassword}
                            autoComplete="new-password"
                            required
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              </div>

              <div className="flex gap-3 p-5 border-t border-slate-100 bg-slate-50/60 shrink-0">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  icon={isSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                  disabled={isSaving}
                >
                  {isSaving ? 'Guardando…' : t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};